import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { WavesDivider } from './WavesDivider';

type TransitionVariant =
  | 'hero-to-venues'
  | 'venues-to-how'
  | 'how-to-featured'
  | 'featured-to-testimonials';

interface ScrollTransitionProps {
  variant: TransitionVariant;
}

export const ScrollTransition = React.memo(function ScrollTransition({
  variant,
}: ScrollTransitionProps) {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();

  const colorMap: Record<TransitionVariant, string[]> = {
    'hero-to-venues': [
      theme.palette.venue.gaming,
      theme.palette.venue.snooker,
      theme.palette.venue.kids,
    ],
    'venues-to-how': [theme.palette.venue.snooker],
    'how-to-featured': [theme.palette.venue.gaming],
    'featured-to-testimonials': [theme.palette.venue.kids],
  };

  const colors = colorMap[variant];

  if (shouldReduceMotion) {
    return <WavesDivider color={colors[0]} />;
  }

  return (
    <Box
      aria-hidden="true"
      role="presentation"
      sx={{
        position: 'relative',
        width: '100%',
        height: 80,
        overflow: 'hidden',
      }}
    >
      {colors.map((color, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{
            duration: 0.6,
            delay: i * 0.15,
            ease: [0.22, 1, 0.36, 1],
          }}
          style={{ position: 'absolute', inset: 0 }}
        >
          <WavesDivider color={color} height={80} />
        </motion.div>
      ))}
    </Box>
  );
});
